import { type CSSProperties, type ReactNode, useMemo } from 'react';
import { Chart } from '@ics-portal/guardian';
import { type Maybe } from '@models/Maybe';
import { CvssVersionLabel } from '@components/cve/cve-details/types';
import { CveCvssGraphPlaceholder } from './CveCvssGraphPlaceholder';
import { CveCvssRadarMetricPill } from './CveCvssRadarMetricPill';
import {
    buildCvssRadarChartData,
    type CvssRadarChartData,
} from './utils/buildCvssRadarChartData';
import styles from './CveCvssSection.module.scss';

export interface CveCvssRadarChartProps {
    vector: Maybe<string>;
    version: CvssVersionLabel;
}

const SUPPORTED_VERSIONS = [
    CvssVersionLabel.V31,
    CvssVersionLabel.V30,
    CvssVersionLabel.V2,
];

interface RadarPillGridPosition {
    row: number;
    column: number;
    justifySelf: CSSProperties['justifySelf'];
}

const V3_METRIC_GRID_POSITIONS: RadarPillGridPosition[] = [
    { row: 1, column: 2, justifySelf: 'center' },
    { row: 2, column: 3, justifySelf: 'start' },
    { row: 3, column: 3, justifySelf: 'start' },
    { row: 4, column: 3, justifySelf: 'start' },
    { row: 5, column: 2, justifySelf: 'center' },
    { row: 4, column: 1, justifySelf: 'end' },
    { row: 3, column: 1, justifySelf: 'end' },
    { row: 2, column: 1, justifySelf: 'end' },
];

const V2_METRIC_GRID_POSITIONS: RadarPillGridPosition[] = [
    { row: 1, column: 2, justifySelf: 'center' },
    { row: 2, column: 3, justifySelf: 'start' },
    { row: 4, column: 3, justifySelf: 'start' },
    { row: 5, column: 2, justifySelf: 'center' },
    { row: 4, column: 1, justifySelf: 'end' },
    { row: 2, column: 1, justifySelf: 'end' },
];

function getMetricGridPositions(
    version: CvssVersionLabel,
): RadarPillGridPosition[] {
    return version === CvssVersionLabel.V2
        ? V2_METRIC_GRID_POSITIONS
        : V3_METRIC_GRID_POSITIONS;
}

function buildChartConfig(chartData: CvssRadarChartData) {
    const data = {
        labels: chartData.labels,
        datasets: [
            {
                data: chartData.values,
                backgroundColor: 'rgba(244, 48, 48, 0.35)',
                borderColor: 'rgba(244, 48, 48, 0.8)',
                pointBackgroundColor: 'rgba(244, 48, 48, 0.8)',
            },
        ],
    };

    const options = {
        maintainAspectRatio: false,
        scales: {
            r: {
                angleLines: { display: true },
                pointLabels: { display: false },
                suggestedMin: 0,
                suggestedMax: 100,
                ticks: { display: false },
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context: { dataIndex: number }) =>
                        `${chartData.labels[context.dataIndex]}: ${chartData.metricLabels[context.dataIndex]}`,
                },
            },
        },
    };

    return { data, options };
}

export function CveCvssRadarChart({
    vector,
    version,
}: CveCvssRadarChartProps): ReactNode {
    const chartData = useMemo(() => {
        if (!vector || !SUPPORTED_VERSIONS.includes(version)) {
            return null;
        }
        try {
            return buildCvssRadarChartData(vector, version);
        } catch {
            return null;
        }
    }, [vector, version]);

    const chartConfig = useMemo(
        () => (chartData ? buildChartConfig(chartData) : null),
        [chartData],
    );

    if (!chartConfig || !chartData) {
        const message = SUPPORTED_VERSIONS.includes(version)
            ? 'No vector data available'
            : 'Radar chart not available for CVSS v4.0';
        return <CveCvssGraphPlaceholder message={message} />;
    }

    const gridPositions = getMetricGridPositions(version);

    return (
        <div className={`flex-none border-round-lg ${styles.graphChart}`}>
            <div className={styles.radarGrid}>
                {chartData.labels.map((label, index) => (
                    <span
                        key={label}
                        style={{
                            gridRow: gridPositions[index]?.row,
                            gridColumn: gridPositions[index]?.column,
                            justifySelf: gridPositions[index]?.justifySelf,
                        }}
                    >
                        <CveCvssRadarMetricPill
                            label={label}
                            value={chartData.metricLabels[index]}
                            rating={chartData.severityRatings[index]}
                        />
                    </span>
                ))}
                <div className={styles.radarGridChart}>
                    <Chart
                        type="radar"
                        data={chartConfig.data}
                        options={chartConfig.options}
                    />
                </div>
            </div>
        </div>
    );
}
